import type { State } from '@src/redux/configureStore'
import {
  getOnboardedSingleAccountsEntities,
  getOnboardedOrganizationEntities,
  getCurrentAccountID
} from '@src/selectors'
import { createSelector } from 'reselect'
import { isEmpty, map, filter, forEach } from 'lodash'
import type { FilterState } from '@src/redux/reducers/filter/default.state'
import type { FilterOption } from '../models'
import {
  mapAccountToFilterOption,
  mapAOrganizationToFilterOption
} from '../helpers/organization.helper'
import { AccountInfo } from '@src/endpoints/services/base.service'

export const getFilterOptions = (state: State) =>
  state.filter || ({} as FilterState)

export const getSingleAccountsSelector = createSelector(
  [getOnboardedSingleAccountsEntities],
  data => {
    const accounts: FilterOption[] = map(data?.accountUIDs || [], uid =>
      mapAccountToFilterOption((data?.entities?.accounts || {})[uid], true)
    )
    return (
      filter(
        accounts || [],
        a => !(a?.original as AccountInfo)?.is_organization_account
      ) || ([] as FilterOption[])
    )
  }
)

export const getOrganizationSelector = createSelector(
  [getOnboardedOrganizationEntities],
  data =>
    map(data?.organizationUIDs || [], uid =>
      mapAOrganizationToFilterOption(uid, data?.entities)
    ) || ([] as FilterOption[])
)

export const getSelectedAccountName = createSelector(
  [
    getOnboardedOrganizationEntities,
    getOnboardedSingleAccountsEntities,
    getCurrentAccountID
  ],
  (data, singleAccounts, currentAccountId) => {
    const { entities } = data || {}
    const { entities: entitiesSingleAccounts } = singleAccounts || {}
    const { accountsByAccountId: singleAccountsById = {} } =
      entitiesSingleAccounts || {}
    const { accountsByAccountId = {}, organizationsByOrgId = {} } =
      entities || {}
    const dataAccountsByIds = { ...accountsByAccountId, ...singleAccountsById }
    const { account_name, account_id } =
      dataAccountsByIds[currentAccountId] || {}
    const { organization_name, organization_id } =
      organizationsByOrgId[currentAccountId] || {}
    return account_name || organization_name || account_id || organization_id
  }
)

export const getFilterData = createSelector(
  [getOrganizationSelector, getSingleAccountsSelector],
  (organizations, singleAccounts) => {
    const output = [...organizations, ...singleAccounts]
    forEach(organizations || [], org => {
      output.push(...((org?.subItems as FilterOption[]) || []))
    })
    return output
  }
)

const getFilter = (state: State) => state.filter

export const filterActiveSelector = createSelector(
  [getFilter],
  options => !isEmpty(options?.currentAccount?.id)
)
